import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Input, Segmented, Select, Spin, Tooltip } from 'antd'
import {
  CloseOutlined,
  CopyOutlined,
  DownloadOutlined,
  ReloadOutlined,
  RobotOutlined,
  SearchOutlined,
  ColumnHeightOutlined,
} from '@ant-design/icons'
import { Button, toast } from '@crosscode/gravity-components'
import { Virtuoso, type VirtuosoHandle } from 'react-virtuoso'
import { useAuth } from '@/app/AuthProvider'
import {
  analyzeLogs,
  downloadLogFile,
  fetchLogContext,
  fetchLogsMeta,
  formatBytes,
  queryLogs,
  type LogExplorerEntry,
  type LogFileMeta,
} from './logExplorerApi'
import { LogAnalysisView } from './LogAnalysisView'
import styles from './CashTerminalLogsModal.module.css'

interface Props {
  open: boolean
  terminalId: string
  title?: string
  onClose: () => void
}

type InspectorMode = 'details' | 'context'
type AnalyzeMode = 'selected' | 'errors' | 'tail'

const PAGE_SIZE_OPTIONS = [
  { value: 300, label: '300' },
  { value: 500, label: '500' },
  { value: 1000, label: '1000' },
  { value: 'all', label: 'Все' },
] as const

type PageSizeValue = (typeof PAGE_SIZE_OPTIONS)[number]['value']

/** «Все» = хвост окна счётчиков на кассе */
const ALL_PAGE_LIMIT = 12000

function resolvePageLimit(size: PageSizeValue) {
  return size === 'all' ? ALL_PAGE_LIMIT : size
}

const LEVEL_CHIPS = [
  { value: '', label: 'Все', tone: '' as const },
  { value: 'ERROR', label: 'Err', tone: 'err' as const },
  { value: 'WARN', label: 'Warn', tone: 'warn' as const },
  { value: 'INFO', label: 'Info', tone: '' as const },
  { value: 'DEBUG', label: 'Dbg', tone: '' as const },
] as const

const ANALYZE_LABELS: Record<AnalyzeMode, string> = {
  selected: 'выделенная строка',
  errors: 'ошибки с кассы',
  tail: 'хвост файла',
}

function levelTone(level?: string | null): 'err' | 'warn' | 'info' | '' {
  const l = (level || '').toUpperCase()
  if (l === 'FATAL' || l === 'ERROR') return 'err'
  if (l === 'WARN' || l === 'WARNING') return 'warn'
  if (l === 'INFO') return 'info'
  return ''
}

function formatLevel(level?: string | null) {
  return (level || '—').toUpperCase().replace('WARNING', 'WARN')
}

function highlightMessage(text: string, q: string) {
  if (!q.trim()) return text
  const idx = text.toLowerCase().indexOf(q.toLowerCase())
  if (idx < 0) return text
  return (
    <>
      {text.slice(0, idx)}
      <mark className={styles.mark}>{text.slice(idx, idx + q.length)}</mark>
      {text.slice(idx + q.length)}
    </>
  )
}

function countLevelsFromEntries(entries: LogExplorerEntry[]) {
  let errors = 0
  let warnings = 0
  let info = 0
  let debug = 0
  for (const e of entries) {
    const l = (e.level || '').toUpperCase().replace('WARNING', 'WARN')
    if (l === 'ERROR' || l === 'FATAL') errors += 1
    else if (l === 'WARN') warnings += 1
    else if (l === 'INFO') info += 1
    else if (l === 'DEBUG' || l === 'TRACE') debug += 1
  }
  const total = errors + warnings + info + debug
  return { total, errors, warnings, info, debug, window: entries.length }
}

function isUsefulCounts(c: {
  total: number
  errors: number
  warnings: number
  info: number
  debug: number
} | null | undefined) {
  if (!c) return false
  return c.errors + c.warnings + c.info + c.debug > 0
}

export function CashTerminalLogsModal({ open, terminalId, title, onClose }: Props) {
  const { hasPermission } = useAuth()
  const canAnalyze = hasPermission('feature.cash.actions.logs_analyze')
  const virtuosoRef = useRef<VirtuosoHandle>(null)
  const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const searchInputRef = useRef<HTMLInputElement | null>(null)
  const bootRef = useRef(0)
  const filtersReady = useRef(false)

  const [files, setFiles] = useState<LogFileMeta[]>([])
  const [filePath, setFilePath] = useState<string | undefined>()
  const [entries, setEntries] = useState<LogExplorerEntry[]>([])
  const [nextCursor, setNextCursor] = useState<string | null>(null)
  const [hasMore, setHasMore] = useState(false)
  const [loading, setLoading] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const [level, setLevel] = useState('')
  const [pageSize, setPageSize] = useState<PageSizeValue>(300)
  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')
  const [wrap, setWrap] = useState(true)
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [inspector, setInspector] = useState<InspectorMode>('details')
  const [rawOpen, setRawOpen] = useState(false)
  const [contextEntries, setContextEntries] = useState<LogExplorerEntry[]>([])
  const [contextLoading, setContextLoading] = useState(false)
  const [analyzing, setAnalyzing] = useState(false)
  const [analysis, setAnalysis] = useState<string | null>(null)
  const [aiOpen, setAiOpen] = useState(false)
  const [analyzeMode, setAnalyzeMode] = useState<AnalyzeMode | null>(null)
  const [metaLabel, setMetaLabel] = useState('')
  const [windowCounts, setWindowCounts] = useState<{
    total: number
    errors: number
    warnings: number
    info: number
    debug: number
    window: number
  } | null>(null)
  const [matchedTotal, setMatchedTotal] = useState<number | null>(null)
  const queryGen = useRef(0)

  /** Подстраховка: не показывать чужой уровень, если бэкенд отдал лишнее */
  const visibleEntries = useMemo(() => {
    if (!level) return entries
    return entries.filter((e) => {
      const l = (e.level || '').toUpperCase().replace('WARNING', 'WARN')
      if (level === 'ERROR') return l === 'ERROR' || l === 'FATAL'
      if (level === 'WARN') return l === 'WARN'
      if (level === 'INFO') return l === 'INFO'
      if (level === 'DEBUG') return l === 'DEBUG' || l === 'TRACE'
      return true
    })
  }, [entries, level])

  const selected = visibleEntries.find((e) => e.id === selectedId) || null
  const selectedIndex = useMemo(
    () => (selectedId ? visibleEntries.findIndex((e) => e.id === selectedId) : -1),
    [visibleEntries, selectedId],
  )

  const chipCount = (value: string) => {
    if (!windowCounts) return '—'
    if (!value) return windowCounts.total
    if (value === 'ERROR') return windowCounts.errors
    if (value === 'WARN') return windowCounts.warnings
    if (value === 'INFO') return windowCounts.info
    if (value === 'DEBUG') return windowCounts.debug
    return 0
  }

  const loadQuery = useCallback(
    async (opts: {
      file?: string
      cursor?: string | null
      append?: boolean
      level?: string
      q?: string
      fileLabel?: string
      limit?: number
    }) => {
      const append = !!opts.append
      const gen = ++queryGen.current
      if (append) setLoadingMore(true)
      else {
        setLoading(true)
        setEntries([])
        setSelectedId(null)
        setNextCursor(null)
        setMatchedTotal(null)
      }
      try {
        const data = await queryLogs(terminalId, {
          file: opts.file,
          cursor: opts.cursor,
          limit: opts.limit ?? resolvePageLimit(pageSize),
          level: opts.level,
          q: opts.q,
          order: 'desc',
        })
        if (gen !== queryGen.current) return

        setEntries((prev) => (append ? [...prev, ...data.entries] : data.entries))
        setNextCursor(data.nextCursor)
        setHasMore(data.hasMore)

        // Чипы: только осмысленные счётчики; нули с бэка не затирают хорошие значения
        if (isUsefulCounts(data.windowCounts)) {
          setWindowCounts(data.windowCounts!)
        } else if (!opts.level && !opts.q && !append) {
          const fromPage = countLevelsFromEntries(data.entries)
          if (isUsefulCounts(fromPage)) {
            setWindowCounts(fromPage)
          } else if (data.pageStats) {
            const ps = data.pageStats
            const fromStats = {
              total:
                (ps.errors || 0) + (ps.warnings || 0) + (ps.info || 0) + (ps.debug || 0),
              errors: ps.errors || 0,
              warnings: ps.warnings || 0,
              info: ps.info || 0,
              debug: ps.debug || 0,
              window: ps.window || data.entries.length,
            }
            if (isUsefulCounts(fromStats)) setWindowCounts(fromStats)
          }
        }

        if (typeof data.pageStats?.matched === 'number') {
          setMatchedTotal(data.pageStats.matched)
        } else if (!append) {
          setMatchedTotal(data.entries.length)
        }
        if (!append) {
          setSelectedId(data.entries[0]?.id ?? null)
          setAnalysis(null)
          setContextEntries([])
          setRawOpen(false)
          setInspector('details')
        }
        if (data.file && opts.fileLabel) setMetaLabel(opts.fileLabel)
        else if (data.file) setMetaLabel(data.file.name)
      } catch (err) {
        if (gen !== queryGen.current) return
        toast.error(err instanceof Error ? err.message : 'Ошибка загрузки логов')
        if (!append) setEntries([])
      } finally {
        if (gen === queryGen.current) {
          setLoading(false)
          setLoadingMore(false)
        }
      }
    },
    [terminalId, pageSize],
  )

  const reload = useCallback(async () => {
    try {
      const meta = await fetchLogsMeta(terminalId)
      setFiles(meta.files)
      const current =
        meta.files.find((f) => f.path === filePath) || meta.files.find((f) => f.current) || meta.files[0]
      const path = current?.path
      setFilePath(path)
      const label = current
        ? `${current.name} · ${formatBytes(current.size)}${current.mtime ? ` · ${new Date(current.mtime).toLocaleString('ru-RU')}` : ''}`
        : ''
      setMetaLabel(label)
      if (path) {
        await loadQuery({ file: path, level, q: search, fileLabel: label })
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Ошибка метаданных')
    }
  }, [terminalId, filePath, level, search, loadQuery])

  useEffect(() => {
    if (!open) return
    bootRef.current += 1
    const boot = bootRef.current
    filtersReady.current = false
    setEntries([])
    setSelectedId(null)
    setAnalysis(null)
    setAiOpen(false)
    setAnalyzeMode(null)
    setSearchInput('')
    setSearch('')
    setLevel('')
    setInspector('details')
    setFilePath(undefined)
    setWindowCounts(null)
    setMatchedTotal(null)
    ;(async () => {
      try {
        const meta = await fetchLogsMeta(terminalId)
        if (boot !== bootRef.current) return
        setFiles(meta.files)
        const current = meta.files.find((f) => f.current) || meta.files[0]
        if (!current) return
        const label = `${current.name} · ${formatBytes(current.size)}${current.mtime ? ` · ${new Date(current.mtime).toLocaleString('ru-RU')}` : ''}`
        setFilePath(current.path)
        setMetaLabel(label)
        await loadQuery({ file: current.path, level: '', q: '', fileLabel: label })
        if (boot === bootRef.current) filtersReady.current = true
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Ошибка метаданных')
      }
    })()
  }, [open, terminalId, loadQuery])

  useEffect(() => {
    if (!open) return
    if (searchTimer.current) clearTimeout(searchTimer.current)
    searchTimer.current = setTimeout(() => setSearch(searchInput.trim()), 350)
    return () => {
      if (searchTimer.current) clearTimeout(searchTimer.current)
    }
  }, [searchInput, open])

  useEffect(() => {
    if (!open || !filePath || !filtersReady.current) return
    const f = files.find((x) => x.path === filePath)
    const label = f ? `${f.name} · ${formatBytes(f.size)}` : undefined
    void loadQuery({
      file: filePath,
      level,
      q: search,
      fileLabel: label,
      limit: resolvePageLimit(pageSize),
    })
  }, [level, search, filePath, pageSize, open, loadQuery, files])

  useEffect(() => {
    if (!open) filtersReady.current = false
  }, [open])

  const selectByOffset = useCallback(
    (delta: number) => {
      if (!visibleEntries.length) return
      const next = Math.max(
        0,
        Math.min(visibleEntries.length - 1, (selectedIndex < 0 ? 0 : selectedIndex) + delta),
      )
      const id = visibleEntries[next]?.id
      if (!id) return
      setSelectedId(id)
      setInspector('details')
      virtuosoRef.current?.scrollIntoView({ index: next, align: 'center' })
    },
    [visibleEntries, selectedIndex],
  )

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement)?.tagName
      const typing = tag === 'INPUT' || tag === 'TEXTAREA' || (e.target as HTMLElement)?.isContentEditable

      if (e.key === 'Escape') {
        if (aiOpen) {
          setAiOpen(false)
          return
        }
        onClose()
        return
      }
      if (typing) return

      if (e.key === '/' && !e.ctrlKey && !e.metaKey) {
        e.preventDefault()
        searchInputRef.current?.focus()
        return
      }
      if (e.key === 'j' || e.key === 'ArrowDown') {
        e.preventDefault()
        selectByOffset(1)
        return
      }
      if (e.key === 'k' || e.key === 'ArrowUp') {
        e.preventDefault()
        selectByOffset(-1)
        return
      }
      if (e.key === 'c' && selected) {
        e.preventDefault()
        setInspector('context')
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose, aiOpen, selectByOffset, selected])

  const loadContext = useCallback(async () => {
    if (!selected || !filePath) return
    setContextLoading(true)
    try {
      const data = await fetchLogContext(terminalId, {
        file: filePath,
        lineNo: selected.lineNo,
        before: 50,
        after: 50,
      })
      setContextEntries(data.entries)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Ошибка контекста')
      setContextEntries([])
    } finally {
      setContextLoading(false)
    }
  }, [selected, filePath, terminalId])

  useEffect(() => {
    if (inspector === 'context' && selected) void loadContext()
  }, [inspector, selected, loadContext])

  const exportLogs = async () => {
    try {
      const { blob, filename } = await downloadLogFile(terminalId, {
        file: filePath,
        level: level || undefined,
        q: search || undefined,
      })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      a.click()
      URL.revokeObjectURL(url)
      toast.success('Лог скачан')
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Ошибка скачивания')
    }
  }

  const copySelected = async () => {
    if (!selected) return
    try {
      await navigator.clipboard.writeText(selected.raw || selected.message || '')
      toast.success('Скопировано')
    } catch {
      toast.error('Не удалось скопировать')
    }
  }

  const runAnalyze = async (mode: AnalyzeMode) => {
    setAnalyzing(true)
    setAnalysis(null)
    setAnalyzeMode(mode)
    setAiOpen(true)
    try {
      let data
      if (mode === 'selected') {
        if (!selected) {
          toast.error('Выделите строку в ленте для анализа')
          return
        }
        // Только выделенная строка — не вся лента
        data = await analyzeLogs({
          terminalId,
          mode: 'selection',
          logs: [
            {
              timestamp: selected.timestamp,
              level: selected.level,
              message: selected.message,
              raw: selected.raw,
            },
          ],
        })
      } else {
        data = await analyzeLogs({
          terminalId,
          mode,
          file: filePath,
          maxLines: mode === 'errors' ? 400 : Math.min(resolvePageLimit(pageSize), 500),
        })
      }
      setAnalysis(data.analysis || 'Пустой ответ')
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Ошибка анализа')
    } finally {
      setAnalyzing(false)
    }
  }

  if (!open) return null

  const terminalLabel = title?.replace(/^Касса\s+/i, '') || `ID ${terminalId}`
  const levelFilterLabel =
    LEVEL_CHIPS.find((c) => c.value === level)?.label || (level ? level : null)
  const footerLeft = (() => {
    if (loading && entries.length === 0) return 'Загрузка…'
    if (level || search) {
      const found = matchedTotal ?? visibleEntries.length
      const parts = [
        levelFilterLabel ? `фильтр ${levelFilterLabel}` : null,
        search ? `поиск «${search}»` : null,
        `найдено ${found}`,
        `показано ${visibleEntries.length}`,
      ].filter(Boolean)
      return parts.join(' · ')
    }
    if (windowCounts) {
      return `хвост ~${windowCounts.window.toLocaleString('ru-RU')} строк · j/k навигация`
    }
    return hasMore ? 'Фильтр и поиск на кассе · j/k навигация' : 'Конец выборки'
  })()

  const content = (
    <div
      className={styles.overlay}
      role="dialog"
      aria-modal="true"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose()
      }}
    >
      <div className={styles.shell}>
        <header className={styles.header}>
          <div className={styles.headerMain}>
            <div className={styles.headerRow}>
              <h2 className={styles.headerTitle}>Касса {terminalLabel}</h2>
            </div>
            <div className={styles.headerMeta}>{metaLabel || 'лог-файл'}</div>
          </div>
          {files.length > 1 ? (
            <Select
              size="small"
              className={styles.fileSelect}
              value={filePath}
              onChange={(v) => setFilePath(v)}
              getPopupContainer={() => document.body}
              styles={{ popup: { root: { zIndex: 1400 } } }}
              options={files.map((f) => ({
                value: f.path,
                label: `${f.name} (${formatBytes(f.size)})`,
              }))}
            />
          ) : null}
          <button type="button" className={styles.closeBtn} onClick={onClose} aria-label="Закрыть">
            <CloseOutlined />
          </button>
        </header>

        <div className={styles.command}>
          <div className={styles.searchWrap}>
            <Input
              allowClear
              size="middle"
              prefix={<SearchOutlined style={{ color: 'var(--text-tertiary)' }} />}
              placeholder="Текст, модуль, exception…  (/)"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className={styles.searchInput}
              ref={(node) => {
                searchInputRef.current = node?.input ?? null
              }}
            />
          </div>

          <div className={styles.chips} role="group" aria-label="Уровень">
            {LEVEL_CHIPS.map((chip) => {
              const active = level === chip.value
              const count = chipCount(chip.value)
              return (
                <button
                  key={chip.value || 'all'}
                  type="button"
                  title={
                    windowCounts
                      ? `В хвосте файла (~${windowCounts.window.toLocaleString('ru-RU')} строк)`
                      : 'Счётчик по хвосту файла'
                  }
                  className={[
                    styles.chip,
                    active ? styles.chipActive : '',
                    chip.tone === 'err' ? styles.chipErr : '',
                    chip.tone === 'warn' ? styles.chipWarn : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  onClick={() => setLevel(chip.value)}
                >
                  {chip.label}
                  <span className={styles.chipCount}>{count}</span>
                </button>
              )
            })}
            {hasMore ? (
              <span className={styles.moreHint} title="В файле на кассе есть строки старше текущей выборки">
                есть ещё на кассе
              </span>
            ) : null}
          </div>

          <div className={styles.pageSize}>
            <span className={styles.pageSizeLabel}>Строк</span>
            <Segmented
              size="small"
              value={pageSize}
              options={PAGE_SIZE_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
              onChange={(v) => setPageSize(v as PageSizeValue)}
            />
          </div>

          <div className={styles.actions}>
            <Tooltip
              title="Перенос длинных строк"
              placement="bottom"
              mouseEnterDelay={0.2}
              getPopupContainer={() => document.body}
              zIndex={1400}
            >
              <button
                type="button"
                className={[styles.iconToggle, wrap ? styles.iconToggleOn : ''].join(' ')}
                onClick={() => setWrap((v) => !v)}
                aria-pressed={wrap}
                aria-label="Перенос строк"
                title="Перенос длинных строк"
              >
                <ColumnHeightOutlined />
              </button>
            </Tooltip>
            <Tooltip
              title="Обновить логи с кассы"
              placement="bottom"
              mouseEnterDelay={0.2}
              getPopupContainer={() => document.body}
              zIndex={1400}
            >
              <span className={styles.tipWrap}>
                <button
                  type="button"
                  className={styles.iconToggle}
                  onClick={() => void reload()}
                  disabled={loading}
                  aria-label="Обновить логи с кассы"
                  title="Обновить логи с кассы"
                >
                  <ReloadOutlined spin={loading} />
                </button>
              </span>
            </Tooltip>
            <Tooltip
              title="Скачать текущий лог-файл"
              placement="bottom"
              mouseEnterDelay={0.2}
              getPopupContainer={() => document.body}
              zIndex={1400}
            >
              <button
                type="button"
                className={styles.iconToggle}
                onClick={() => void exportLogs()}
                aria-label="Скачать текущий лог-файл"
                title="Скачать текущий лог-файл"
              >
                <DownloadOutlined />
              </button>
            </Tooltip>
            {canAnalyze ? (
              <Tooltip
                title="Открыть панель ИИ-анализа логов"
                placement="bottom"
                mouseEnterDelay={0.2}
                getPopupContainer={() => document.body}
                zIndex={1400}
              >
                <button
                  type="button"
                  className={styles.analyzeBtn}
                  disabled={analyzing}
                  title="Открыть панель ИИ-анализа логов"
                  aria-label="Анализ логов"
                  aria-expanded={aiOpen}
                  onClick={() => setAiOpen(true)}
                >
                  <RobotOutlined />
                  <span>{analyzing ? 'Анализ…' : 'Анализ'}</span>
                </button>
              </Tooltip>
            ) : null}
          </div>
        </div>

        <div className={[styles.body, aiOpen ? styles.bodyWithAi : ''].join(' ')}>
          <div className={styles.listPane}>
            <div className={[styles.viewer, wrap ? styles.wrap : styles.nowrap].join(' ')}>
              {loading && visibleEntries.length === 0 ? (
                <div className={styles.empty}>
                  <Spin />
                  <span>Загрузка логов с кассы…</span>
                </div>
              ) : visibleEntries.length === 0 ? (
                <div className={styles.empty}>
                  <div className={styles.emptyTitle}>Нет записей</div>
                  <span>Сбросьте фильтр или измените поисковый запрос</span>
                  {(level || search) && (
                    <Button
                      size="small"
                      onClick={() => {
                        setLevel('')
                        setSearchInput('')
                        setSearch('')
                      }}
                    >
                      Сбросить фильтры
                    </Button>
                  )}
                </div>
              ) : (
                <Virtuoso
                  ref={virtuosoRef}
                  style={{ height: '100%' }}
                  data={visibleEntries}
                  overscan={200}
                  itemContent={(_index, log) => {
                    const tone = levelTone(log.level)
                    const selectedRow = log.id === selectedId
                    return (
                      <div
                        className={[
                          styles.entry,
                          selectedRow ? styles.entrySelected : '',
                          tone === 'err' ? styles.entryErr : '',
                          tone === 'warn' ? styles.entryWarn : '',
                        ]
                          .filter(Boolean)
                          .join(' ')}
                        onClick={() => {
                          setSelectedId(log.id)
                          setInspector('details')
                          setRawOpen(false)
                        }}
                      >
                        <span className={styles.gutter} aria-hidden />
                        <span className={styles.ts}>{log.timestamp || '—'}</span>
                        <span
                          className={[
                            styles.lvl,
                            tone === 'err' ? styles.lvlErr : '',
                            tone === 'warn' ? styles.lvlWarn : '',
                            tone === 'info' ? styles.lvlInfo : '',
                          ]
                            .filter(Boolean)
                            .join(' ')}
                        >
                          {formatLevel(log.level)}
                        </span>
                        {log.module ? (
                          <button
                            type="button"
                            className={styles.mod}
                            title={`Фильтр: ${log.module}`}
                            onClick={(e) => {
                              e.stopPropagation()
                              setSearchInput(log.module || '')
                            }}
                          >
                            {log.module}
                          </button>
                        ) : (
                          <span className={styles.mod} />
                        )}
                        <span className={styles.msg}>
                          {highlightMessage(log.message || log.raw || '—', search)}
                        </span>
                      </div>
                    )
                  }}
                />
              )}
            </div>
            <div className={styles.listFooter}>
              <span className={styles.footerHint}>{footerLeft}</span>
              {hasMore ? (
                <button
                  type="button"
                  className={styles.loadMore}
                  disabled={loadingMore || loading}
                  onClick={() =>
                    void loadQuery({
                      file: filePath,
                      cursor: nextCursor,
                      append: true,
                      level,
                      q: search,
                    })
                  }
                >
                  {loadingMore ? 'Загрузка…' : 'Загрузить ещё · старше'}
                </button>
              ) : (
                <span className={styles.footerHint}>Конец выборки</span>
              )}
            </div>
          </div>

          <aside className={styles.inspector}>
            <div className={styles.inspectorHead}>
              <button
                type="button"
                className={[styles.inspTab, inspector === 'details' ? styles.inspTabActive : ''].join(' ')}
                onClick={() => setInspector('details')}
              >
                Строка
              </button>
              <button
                type="button"
                className={[styles.inspTab, inspector === 'context' ? styles.inspTabActive : ''].join(' ')}
                onClick={() => setInspector('context')}
              >
                Контекст
              </button>
            </div>
            <div className={styles.inspectorBody}>
              {inspector === 'details' ? (
                selected ? (
                  <>
                    <div className={styles.detailHero}>
                      <span
                        className={[
                          styles.lvl,
                          levelTone(selected.level) === 'err' ? styles.lvlErr : '',
                          levelTone(selected.level) === 'warn' ? styles.lvlWarn : '',
                          levelTone(selected.level) === 'info' ? styles.lvlInfo : '',
                        ]
                          .filter(Boolean)
                          .join(' ')}
                      >
                        {formatLevel(selected.level)}
                      </span>
                      <span className={styles.detailTime}>{selected.timestamp || '—'}</span>
                      {selected.module ? (
                        <span className={styles.detailModule}>{selected.module}</span>
                      ) : null}
                    </div>
                    <div className={styles.detailBlock}>
                      <div className={styles.detailLabel}>Сообщение</div>
                      <div className={styles.detailMono}>{selected.message || '—'}</div>
                    </div>
                    <div className={styles.detailBlock}>
                      <button
                        type="button"
                        className={styles.rawToggle}
                        onClick={() => setRawOpen((v) => !v)}
                      >
                        {rawOpen ? '▾ Сырая строка' : '▸ Сырая строка'}
                      </button>
                      {rawOpen ? (
                        <div className={`${styles.detailMono} ${styles.rawBody}`}>
                          {selected.raw || '—'}
                        </div>
                      ) : null}
                    </div>
                    <div className={styles.inspActions}>
                      <Button size="small" icon={<CopyOutlined />} onClick={() => void copySelected()}>
                        Копировать
                      </Button>
                      <Button size="small" onClick={() => setInspector('context')}>
                        Контекст ±50
                      </Button>
                      {canAnalyze ? (
                        <Button
                          size="small"
                          type="primary"
                          icon={<RobotOutlined />}
                          loading={analyzing}
                          onClick={() => {
                            setAiOpen(true)
                            void runAnalyze('selected')
                          }}
                        >
                          Спросить ИИ
                        </Button>
                      ) : null}
                    </div>
                  </>
                ) : (
                  <div className={styles.sideEmpty}>Выберите строку в ленте</div>
                )
              ) : null}

              {inspector === 'context' ? (
                contextLoading ? (
                  <div className={styles.sideEmpty}>
                    <Spin /> Загрузка контекста…
                  </div>
                ) : contextEntries.length === 0 ? (
                  <div className={styles.sideEmpty}>
                    {selected ? 'Нет контекста' : 'Сначала выберите строку'}
                  </div>
                ) : (
                  <>
                    <div className={styles.ctxMeta}>
                      строка {selected?.lineNo ?? '—'} · ±{Math.floor(contextEntries.length / 2)}
                    </div>
                    {contextEntries.map((line) => (
                      <div
                        key={line.id}
                        className={[styles.ctxLine, line.focus ? styles.ctxLineFocus : ''].join(' ')}
                      >
                        <span className={styles.ctxNo}>{line.lineNo}</span>
                        <span>{line.raw || line.message}</span>
                      </div>
                    ))}
                  </>
                )
              ) : null}
            </div>
          </aside>

          {aiOpen ? (
            <aside className={styles.aiDock}>
              <div className={styles.aiHead}>
                <div className={styles.aiTitle}>
                  Анализ ИИ
                  {analyzeMode ? (
                    <div className={styles.aiMode}>на основе: {ANALYZE_LABELS[analyzeMode]}</div>
                  ) : null}
                </div>
                <button
                  type="button"
                  className={styles.aiClose}
                  onClick={() => setAiOpen(false)}
                  aria-label="Закрыть анализ"
                >
                  <CloseOutlined />
                </button>
              </div>
              <div className={styles.aiBody}>
                {analyzing ? (
                  <div className={styles.aiSkeleton}>
                    <div className={styles.skelBlock} />
                    <div className={styles.skelBlock} />
                    <div className={styles.skelBlock} />
                  </div>
                ) : (
                  <>
                    <div className={styles.aiModes}>
                      <div className={styles.aiModesLabel}>Что анализировать</div>
                      <div className={styles.aiQuick}>
                        <button
                          type="button"
                          className={[
                            styles.aiModeBtn,
                            analyzeMode === 'selected' ? styles.aiModeBtnActive : '',
                          ].join(' ')}
                          disabled={analyzing || !selected}
                          onClick={() => void runAnalyze('selected')}
                        >
                          <strong>Выделенная строка</strong>
                          <span>
                            {selected
                              ? `${formatLevel(selected.level)} · ${(selected.message || selected.raw || '').slice(0, 60)}`
                              : 'Сначала выберите строку в ленте'}
                          </span>
                        </button>
                        <button
                          type="button"
                          className={[
                            styles.aiModeBtn,
                            analyzeMode === 'errors' ? styles.aiModeBtnActive : '',
                          ].join(' ')}
                          disabled={analyzing}
                          onClick={() => void runAnalyze('errors')}
                        >
                          <strong>Только ошибки</strong>
                          <span>ERROR/FATAL с кассы</span>
                        </button>
                        <button
                          type="button"
                          className={[
                            styles.aiModeBtn,
                            analyzeMode === 'tail' ? styles.aiModeBtnActive : '',
                          ].join(' ')}
                          disabled={analyzing}
                          onClick={() => void runAnalyze('tail')}
                        >
                          <strong>Хвост файла</strong>
                          <span>Последние строки лога</span>
                        </button>
                      </div>
                    </div>
                    {analysis ? (
                      <div className={styles.aiResult}>
                        <LogAnalysisView markdown={analysis} />
                      </div>
                    ) : (
                      <p className={styles.aiEmptyHint}>
                        Выберите режим выше — ИИ вернёт критичность, симптомы и шаги устранения.
                      </p>
                    )}
                  </>
                )}
              </div>
            </aside>
          ) : null}
        </div>
      </div>
    </div>
  )

  return createPortal(content, document.body)
}
